import PropTypes from "prop-types";
import React, { useRef } from "react";

import { useDeviceInfo, useResizeObserver } from "@swa-ui/browser";
import { Badge, Link } from "@swa-ui/core";
import { fireDestinationOffersAnalytics } from "@swa-ui/dotcom-analytics";
import { applyOverrides } from "@swa-ui/placements";

import styles from "./DestinationByOriginCard.module.scss";

export const DestinationByOriginCard = (props) => {
  const { analyticsData, item, onImageLoad } = props;
  const { badges, graphic, link, text } = item;
  const imageRef = useRef(null);
  const { screenSize } = useDeviceInfo();

  useResizeObserver({ callback: onImageLoad, element: imageRef });

  return (
    <>
      {link && <Link {...getLinkProps()}>{renderGraphicImagePath()}</Link>}
      {renderDestinationBadges()}
      {text && renderDestinationText()}
    </>
  );

  function renderGraphicImagePath() {
    const { altText, imagePath } = applyOverrides(graphic, screenSize);

    return (
      imagePath && (
        <img
          alt={altText}
          className={styles.destinationByOriginImage}
          ref={imageRef}
          src={imagePath}
        />
      )
    );
  }

  function renderDestinationBadges() {
    return (
      badges && <div className={styles.destinationByOriginBadgesContainer}>{renderBadge()}</div>
    );
  }

  function renderBadge() {
    return badges.map((badge) => <Badge key={badge.value} {...getBadgeProps(badge)} />);
  }

  function renderDestinationText() {
    const { destination, priceText, travelDetails } = text;

    return (
      <div className={styles.destinationByOriginTextContainer}>
        {destination?.value && <p {...getStyleProps(destination)}>{destination.value}</p>}
        {travelDetails?.value && <p {...getStyleProps(travelDetails)}>{travelDetails.value}</p>}
        {priceText && renderPricingText()}
      </div>
    );
  }

  function renderPricingText() {
    const { priceValue, priceText } = text;

    return (
      <div className={styles.destinationByOriginPricingContainer} {...getStyleProps(priceText)}>
        {priceText.value}
        {priceValue?.value && (
          <span className={styles.destinationByOriginPrice} {...getStyleProps(priceValue)}>
            {priceValue.value}
          </span>
        )}
      </div>
    );
  }

  function getLinkProps() {
    const { ariaLabel, newWindow, target } = link;

    return {
      "aria-label": ariaLabel,
      className: styles.destinationByOriginLink,
      href: target,
      newWindow,
      onClick: () => fireDestinationOffersAnalytics(analyticsData),
    };
  }

  function getBadgeProps(badge) {
    const { badgeStyle, badgeType, foregroundColor, value } = badge;

    return {
      children: <span {...getStyleProps(badge)}>{value}</span>,
      className: styles.destinationByOriginTag,
      foregroundColor,
      padding: "xsmall",
      styleType: badgeStyle,
      type: badgeType,
    };
  }

  function getStyleProps(textType) {
    const { color, fontSize, fontWeight } = textType;

    return {
      style: {
        color,
        fontSize,
        fontWeight,
      },
    };
  }
};

DestinationByOriginCard.displayName = "DestinationByOriginCard";
DestinationByOriginCard.propTypes = {
  /** Object that contains analytics related data for an item */
  analyticsData: PropTypes.shape({
    destination: PropTypes.string,
    lfmaPrice: PropTypes.string,
    origin: PropTypes.string,
  }),

  /** Object that contains list of content items to be rendered as DestinationByOrigin card components. */
  item: PropTypes.shape({
    /** Object that contains list of badges props. */
    badges: PropTypes.arrayOf(
      PropTypes.shape({
        badgeStyle: PropTypes.string,
        badgeType: PropTypes.string,
        color: PropTypes.string,
        fontSize: PropTypes.string,
        fontWeight: PropTypes.string,
        value: PropTypes.string.isRequired,
      })
    ),

    /** Object that contains DestinationByOrigin card image props. */
    graphic: PropTypes.shape({
      altText: PropTypes.string,
      imagePath: PropTypes.string,
      overrides: PropTypes.shape({
        large: PropTypes.shape({
          altText: PropTypes.string,
          imagePath: PropTypes.string.isRequired,
        }),
        medium: PropTypes.shape({
          altText: PropTypes.string,
          imagePath: PropTypes.string.isRequired,
        }),
        small: PropTypes.shape({
          altText: PropTypes.string,
          imagePath: PropTypes.string.isRequired,
        }),
      }),
    }),

    /** Object that contains link props. */
    link: PropTypes.shape({
      ariaLabel: PropTypes.string,
      newWindow: PropTypes.bool,
      target: PropTypes.string.isRequired,
    }),

    /** Object that contains DestinationByOrigin card text props. */
    text: PropTypes.shape({
      destination: PropTypes.shape({
        color: PropTypes.string,
        fontSize: PropTypes.string,
        fontWeight: PropTypes.string,
        value: PropTypes.string.isRequired,
      }),
      priceText: PropTypes.shape({
        color: PropTypes.string,
        fontSize: PropTypes.string,
        fontWeight: PropTypes.string,
        value: PropTypes.string.isRequired,
      }),
      priceValue: PropTypes.shape({
        color: PropTypes.string,
        fontSize: PropTypes.string,
        fontWeight: PropTypes.string,
        value: PropTypes.string.isRequired,
      }),
      travelDetails: PropTypes.shape({
        color: PropTypes.string,
        fontSize: PropTypes.string,
        fontWeight: PropTypes.string,
        value: PropTypes.string.isRequired,
      }),
    }),
  }).isRequired,
};
